import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCompatRouter } from '@/src/lib/router-compat/useCompatRouter';
import { serializePanelToUrl } from './panelUrlSerializer';

export type PanelContentProps = {
  panelProps?: Record<string, unknown>;
};

export type PanelUrlIntent = 'none' | 'replace' | 'push';

export type PanelUrlConfig = {
  url: string;
};

export type PanelEntry = {
  id: string;
  title: string;
  content: React.ComponentType<PanelContentProps>;
  panelProps?: Record<string, unknown>;
  urlIntent?: PanelUrlIntent;
  urlConfig?: PanelUrlConfig;
};

export type PanelNavigationContextValue = {
  stack: Array<PanelEntry>;
  activePanel: PanelEntry | null;
  hasPanels: boolean;
  pushPanel: (panel: PanelEntry) => void;
  popPanel: () => void;
  clearPanels: () => void;
  /** Scroll position captured synchronously at pushPanel call time, before React commits DOM changes. */
  scrollCaptureRef: React.RefObject<number>;
};

export const PanelNavigationContext = createContext<PanelNavigationContextValue | null>(null);

/**
 * Applies the URL side-effect for a panel's urlIntent.
 * Uses window.history directly to avoid triggering Next.js route changes,
 * matching the pattern in useBorderToggle and useDetectedAreaUrlSync.
 */
function applyUrlIntent(panelId: string | null, intent: PanelUrlIntent): void {
  if (typeof window === 'undefined') return;
  if (intent === 'none') return;

  const newUrl = serializePanelToUrl(panelId);
  if (intent === 'replace') {
    window.history.replaceState(null, '', newUrl);
  } else {
    window.history.pushState(null, '', newUrl);
  }
}

export function PanelNavigationProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<Array<PanelEntry>>([]);
  const router = useCompatRouter();

  // Ref to read current stack synchronously in callbacks without stale closures.
  // Same pattern as filtersRef in FilterProvider.
  const stackRef = useRef(stack);
  stackRef.current = stack;

  // Stores the URL before the first urlConfig panel was pushed,
  // so clearPanels/popPanel can restore it.
  const previousUrlRef = useRef<string | null>(null);

  // Scroll position captured synchronously in pushPanel, before React commits DOM changes.
  // Reading window.scrollY after React applies display:none triggers a forced reflow that
  // clamps the value to 0. Capturing here (at interaction time) guarantees accuracy.
  const scrollCaptureRef = useRef(0);

  // Auto-clear panels when the pathname changes (page navigation),
  // but NOT on query-param-only changes (pagination, filters via shallow routing).
  // Uses asPath (not pathname) because SRP and HDP share the same route template.
  const previousPathRef = useRef(router.asPath.split('?')[0]);

  useEffect(() => {
    const currentPath = router.asPath.split('?')[0];
    if (currentPath !== previousPathRef.current) {
      previousPathRef.current = currentPath;
      // No URL cleanup needed here — the browser already shows the new page's URL
      previousUrlRef.current = null;
      setStack([]);
    }
  }, [router.asPath]);

  const pushPanel = useCallback((panel: PanelEntry) => {
    // Capture scroll position NOW — before React batches the state update and
    // commits DOM changes (display:none) that would clamp window.scrollY to 0.
    if (typeof window !== 'undefined') {
      scrollCaptureRef.current = window.scrollY;
    }

    if (panel.urlConfig) {
      const hasExistingUrlConfig = stackRef.current.some((p) => p.urlConfig);

      if (typeof window !== 'undefined') {
        if (!hasExistingUrlConfig) {
          previousUrlRef.current = window.location.pathname + window.location.search + window.location.hash;
        }
        // Every urlConfig panel gets its own history entry so browser-back
        // cycles through HDPs one at a time.
        window.history.pushState(null, '', panel.urlConfig.url);
      }

      setStack((prev) => [...prev, panel]);
    } else {
      setStack((prev) => [...prev, panel]);
      applyUrlIntent(panel.id, panel.urlIntent ?? 'none');
    }
  }, []);

  const popPanel = useCallback(() => {
    const current = stackRef.current;
    if (current.length === 0) return;

    const popped = current[current.length - 1];

    setStack((prev) => prev.slice(0, -1));

    if (popped.urlConfig) {
      // Restore previous URL if no remaining urlConfig panels
      const remaining = current.slice(0, -1);
      const hasRemainingUrlConfig = remaining.some((p) => p.urlConfig);
      if (!hasRemainingUrlConfig && previousUrlRef.current) {
        window.history.replaceState(null, '', previousUrlRef.current);
        previousUrlRef.current = null;
      }
    } else {
      const poppedIntent = popped.urlIntent ?? 'none';
      if (poppedIntent !== 'none') {
        const remaining = current.slice(0, -1);
        const nextUrlPanel = remaining.findLast((p) => (p.urlIntent ?? 'none') !== 'none');
        applyUrlIntent(nextUrlPanel?.id ?? null, 'replace');
      }
    }
  }, []);

  const clearPanels = useCallback(() => {
    const current = stackRef.current;
    const hadUrlConfigPanel = current.some((p) => p.urlConfig);
    const hadUrlIntentPanel = current.some((p) => (p.urlIntent ?? 'none') !== 'none');

    stackRef.current = [];
    setStack([]);

    if (hadUrlConfigPanel && previousUrlRef.current) {
      window.history.replaceState(null, '', previousUrlRef.current);
      previousUrlRef.current = null;
    } else if (hadUrlIntentPanel) {
      applyUrlIntent(null, 'replace');
    }

    router.beforePopState(() => true);
  }, [router]);

  // When a urlConfig panel is open, two separate effects handle browser-back:
  //
  // 1. beforePopState(() => false) — prevents Next.js from processing the
  //    popstate as a route change (which would trigger re-renders and scroll reset)
  //
  // 2. Raw popstate listener — clears the panel stack and restores the URL.
  //    Separated from beforePopState because state updates inside the
  //    beforePopState callback don't reliably trigger React re-renders.
  const hasUrlConfigPanel = stack.some((p) => p.urlConfig);

  useEffect(() => {
    if (!hasUrlConfigPanel) {
      router.beforePopState(() => true);
      return;
    }

    router.beforePopState(() => false);

    return () => {
      router.beforePopState(() => true);
    };
  }, [hasUrlConfigPanel, router]);

  useEffect(() => {
    if (!hasUrlConfigPanel) return;

    const handlePopState = () => {
      const current = stackRef.current;

      // Find the topmost urlConfig panel
      let lastUrlConfigIdx = -1;
      for (let i = current.length - 1; i >= 0; i--) {
        if (current[i].urlConfig) {
          lastUrlConfigIdx = i;
          break;
        }
      }

      if (lastUrlConfigIdx === -1) {
        // No urlConfig panels left — let Next.js handle normally
        previousUrlRef.current = null;
        stackRef.current = [];
        setStack([]);
        router.beforePopState(() => true);
        return;
      }

      // Pop the topmost urlConfig panel and any non-urlConfig children above it
      const newStack = current.slice(0, lastUrlConfigIdx);
      stackRef.current = newStack;
      setStack(newStack);

      // If no urlConfig panels remain, restore normal Next.js popstate handling
      if (!newStack.some((p) => p.urlConfig)) {
        previousUrlRef.current = null;
        router.beforePopState(() => true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUrlConfigPanel, router]);

  // Prevent browser auto-scroll on history.back() while urlConfig panels are open.
  // Without this, the browser resets scroll position before our cleanup can restore it.
  useEffect(() => {
    if (!hasUrlConfigPanel) return;

    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = prev;
    };
  }, [hasUrlConfigPanel]);

  const value = useMemo<PanelNavigationContextValue>(
    () => ({
      stack,
      activePanel: stack.length > 0 ? stack[stack.length - 1] : null,
      hasPanels: stack.length > 0,
      pushPanel,
      popPanel,
      clearPanels,
      scrollCaptureRef,
    }),
    [stack, pushPanel, popPanel, clearPanels],
  );

  return <PanelNavigationContext.Provider value={value}>{children}</PanelNavigationContext.Provider>;
}

export function usePanelNavigation(): PanelNavigationContextValue {
  const context = useContext(PanelNavigationContext);
  if (!context) {
    throw new Error('usePanelNavigation must be used within a PanelNavigationProvider');
  }
  return context;
}
